import { useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardActionArea, CardContent, Stack, Typography, alpha } from "@mui/material";
import { AppButton, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid, AppSlideOver, PageSectionCard } from "../../../components/layout";
import { GRID_FULL } from "../../../lib/ui/formLayout";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useTranslation } from "react-i18next";
import { PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import DataTableWithPagination from "../../../components/DataTable";
import { dashboardService } from "../../../services/dashboardService";
import {
  DASHBOARD_LAYOUT_PRESETS,
  type DashboardLayoutPresetId,
} from "../../../utils/dashboardLayoutPresets";
import { formatLimitValue, usePackageLimits } from "../../../hooks/usePackageLimits";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";

function CreateDashboardDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [layoutPresetId, setLayoutPresetId] = useState<DashboardLayoutPresetId>("blank");

  const submit = async () => {
    const preset =
      DASHBOARD_LAYOUT_PRESETS.find((p) => p.id === layoutPresetId) ?? DASHBOARD_LAYOUT_PRESETS[0];
    const created = await dashboardService.create({
      name,
      description,
      layout: {
        gridCols: preset.gridCols,
        rowHeight: preset.rowHeight,
        widgets: [],
        layoutPresetId: preset.id,
        zones: preset.zones,
      },
    });
    onCreated(created.id || "");
    onClose();
  };

  return (
    <AppSlideOver
      open={open}
      onClose={onClose}
      size="md"
      title={t("dashboard.create")}
      actions={
        <>
          <AppButton variant="outlined" onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          <AppButton onClick={() => void submit()} disabled={!name.trim()}>
            {t("common.create")}
          </AppButton>
        </>
      }
    >
      <AppFormGrid>
        <AppFormField size={GRID_FULL}>
          <AppTextField label={t("dashboard.name")} value={name} onChange={(e) => setName(e.target.value)} />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <AppTextField
            label={t("dashboard.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t("dashboard.preset.label")}
          </Typography>
        </AppFormField>
        <AppFormField size={GRID_FULL}>
          <Stack spacing={1}>
            {DASHBOARD_LAYOUT_PRESETS.map((preset) => (
              <Card
                key={preset.id}
                variant="outlined"
                sx={custom.pages.dashboards.layoutPresetCard(layoutPresetId === preset.id)}
              >
                <CardActionArea onClick={() => setLayoutPresetId(preset.id)}>
                  <CardContent sx={custom.pages.dashboards.listPresetCardContent}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t(preset.nameKey)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(preset.descriptionKey)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </AppFormField>
      </AppFormGrid>
    </AppSlideOver>
  );
}

export default function DashboardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tableRef = useRef<any>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const packageLimits = usePackageLimits();

  const columns = [
    {
      name: t("dashboard.name"),
      selector: (row: { name?: string }) => row.name || "",
      sortable: true,
    },
    {
      name: t("dashboard.description"),
      selector: (row: { description?: string }) => row.description || "",
      sortable: false,
    },
  ];

  const createDashboard = () => setCreateOpen(true);

  const handleDelete = useCallback(
    async (row: { id?: string }) => {
      if (!row.id) return;
      await dashboardService.remove(row.id);
    },
    []
  );

  return (
    <PageListContainer>
      <PageHeader
        title={t("dashboard.title")}
        actions={
          <AppButton variant="contained" startIcon={<AddOutlinedIcon />} onClick={createDashboard}>
            {t("dashboard.create")}
          </AppButton>
        }
      />
      {packageLimits && (
        <PackageLimitHint group="count" capped={packageLimits.maxDashboards != null}>
          {t("packageLimits.dashboards", {
            count: formatLimitValue(packageLimits.maxDashboards, t("packageLimits.unlimited")),
          })}
        </PackageLimitHint>
      )}
      <PageSectionCard title={t("dashboard.listTitle")} noPadding>
        <DataTableWithPagination
          ref={tableRef}
          cols={columns}
          fetchUrl="/dashboards"
          handleView={(row: { id?: string }) => row.id && navigate(`/dashboards/${row.id}`)}
          handleEdit={(row: { id?: string }) => row.id && navigate(`/dashboards/${row.id}/edit`)}
          handleDelete={handleDelete}
          searchKey="name"
          searchCollapseToIcon
        />
      </PageSectionCard>
      <CreateDashboardDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          tableRef.current?.refresh();
          navigate(`/dashboards/${id}/edit`);
        }}
      />
    </PageListContainer>
  );
}
